import { useState, useCallback } from "react";
import useScanner from "../../hooks/useScanner";
import ScanModal from "../ScanModal/ScanModal";
import NewProductModal from "../NewProductModal/NewProductModal";
import LotSelectorModal from "../LotSelectorModal/LotSelectorModal";
import ProductSearchModal from "../ProductSearchModal/ProductSearchModal";
import { apiCreateProduct } from "../../services/api";
import { generateTxId } from "../../utils/formatters";
import { parseGS1 } from "../../utils/gs1Parser";
import styles from "./Workflow.module.css";

export default function AddWorkflow({ products, user, onTransaction, onNewProduct, onBack }) {
  const [foundProduct, setFoundProduct] = useState(null);
  const [showQuantity, setShowQuantity] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [showLotSelector, setShowLotSelector] = useState(false);
  const [lotMatches, setLotMatches] = useState([]);
  const [pendingBarcode, setPendingBarcode] = useState("");
  const [pendingGs1, setPendingGs1] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [lastTx, setLastTx] = useState(null);
  const [error, setError] = useState("");

  const handleScan = useCallback((rawBarcode) => {
    setError("");
    setLastTx(null);
    const gs1 = parseGS1(rawBarcode);
    const lookupBarcode = gs1.isGS1 ? gs1.gtin : rawBarcode;
    const matches = products.filter(p => (p.barcode || p.id) === lookupBarcode);

    if (matches.length >= 1) {
      // If we parsed a lot number, check for an exact GTIN + lot match first.
      // If found, skip the lot selector and go straight to the quantity modal.
      if (gs1.isGS1 && gs1.lotNumber) {
        const exactLot = matches.find(p => p.lotNumber === gs1.lotNumber);
        if (exactLot) {
          setFoundProduct(exactLot);
          setShowQuantity(true);
          return;
        }
      }
      // Multiple lots or no exact lot match — show the lot selector.
      // Store gs1 so "Add New Lot" can pre-fill the new lot form.
      setLotMatches(matches.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setPendingBarcode(lookupBarcode);
      setPendingGs1(gs1.isGS1 ? gs1 : null);
      setShowLotSelector(true);
    } else {
      setPendingBarcode(lookupBarcode);
      setPendingGs1(gs1.isGS1 ? gs1 : null);
      setPrefill(null);
      setShowNewProduct(true);
    }
  }, [products]);

  const { error: scanError } = useScanner({
    onScan: handleScan,
    enabled: !showQuantity && !showNewProduct && !showLotSelector && !showSearch,
  });

  const handleLotSelect = (product) => {
    setShowLotSelector(false);
    setFoundProduct(product);
    setShowQuantity(true);
  };

  const handleSearchSelect = (lots) => {
    setShowSearch(false);
    setError("");
    setLastTx(null);
    if (lots.length >= 1) {
      setLotMatches(lots.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setPendingBarcode(lots[0].barcode || lots[0].id);
      setShowLotSelector(true);
    }
  };

  const handleSearchCreateNew = () => {
    setShowSearch(false);
    setPendingBarcode("");
    setPendingGs1(null);
    setPrefill(null);
    setShowNewProduct(true);
  };

  const handleAddNewLot = () => {
    const existing = lotMatches[0];
    setShowLotSelector(false);
    setPrefill({
      type: existing.type,
      description: existing.description,
      manufacturer: existing.manufacturer,
      minOrderLine: existing.minOrderLine,
    });
    setShowNewProduct(true);
  };

  const handleConfirmAdd = async ({ qty, notes }) => {
    const newStock = foundProduct.stock + qty;
    const tx = {
      id: generateTxId(),
      productId: foundProduct.id,
      productName: foundProduct.description,
      manufacturer: foundProduct.manufacturer,
      type: "IN",
      quantity: qty,
      notes,
      performedBy: user.name,
      timestamp: new Date().toISOString(),
      stockAfter: newStock,
    };
    onTransaction(tx, foundProduct.id, newStock);
    setLastTx(tx);
    setShowQuantity(false);
    setFoundProduct(null);
  };

  const handleNewProductSave = async (productData) => {
    try {
      const result = await apiCreateProduct(user.token, productData);
      const newProduct = result.product || { ...productData, stock: 0 };
      onNewProduct(newProduct);
      setShowNewProduct(false);
      setPrefill(null);
      setPendingGs1(null);
      setFoundProduct(newProduct);
      setShowQuantity(true);
    } catch (err) {
      setError(`Failed to create product: ${err.data?.error || err.message}`);
      setShowNewProduct(false);
      setPrefill(null);
      setPendingGs1(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button onClick={onBack} className={styles.backBtn}>← Back</button>
        <div className={styles.workflowTitle}>Add to Inventory</div>
      </div>

      {lastTx && (
        <div className={styles.success}>
          <div className={styles.successLabel}>Added Successfully</div>
          <div className={styles.successName}>{lastTx.productName}</div>
          <div className={styles.successMeta}>+{lastTx.quantity} — New stock: {lastTx.stockAfter}</div>
          <button onClick={() => setLastTx(null)} className={styles.dismissBtn}>dismiss</button>
        </div>
      )}

      {(error || scanError) && (
        <div className={styles.error}>! {error || scanError}</div>
      )}

      <div className={styles.scannerCard}>
        <div className={styles.readyState}>
          <div className={styles.readyIcon}>+</div>
          <div className={styles.readyTitle}>SCAN ITEM TO ADD</div>
          <div className={styles.readyHint}>Point barcode scanner at item</div>
          <div className={styles.readyDivider}>or</div>
          <button onClick={() => setShowSearch(true)} className={styles.manualSearchBtn}>
            Search Manually
          </button>
        </div>
      </div>

      {showLotSelector && (
        <LotSelectorModal lots={lotMatches} mode="add" onSelect={handleLotSelect}
          onAddNewLot={handleAddNewLot}
          onClose={() => { setShowLotSelector(false); setLotMatches([]); }} />
      )}
      {showQuantity && foundProduct && (
        <ScanModal product={foundProduct} mode="add" onConfirm={handleConfirmAdd}
          onClose={() => { setShowQuantity(false); setFoundProduct(null); }} />
      )}
      {showNewProduct && (
        <NewProductModal
          barcode={pendingBarcode}
          prefill={prefill}
          gs1Parsed={pendingGs1}
          onSave={handleNewProductSave}
          onClose={() => {
            setShowNewProduct(false);
            setPendingBarcode("");
            setPrefill(null);
            setPendingGs1(null);
          }}
        />
      )}
      {showSearch && (
        <ProductSearchModal products={products} mode="add" onSelect={handleSearchSelect}
          onCreateNew={handleSearchCreateNew}
          onClose={() => setShowSearch(false)} />
      )}
    </div>
  );
}
